import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, BarChart3, Clock, CheckCircle, XCircle, Plus, Pencil, Trash2,
  ChevronRight, LogOut, Eye, Loader2, Music, Database
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import ConcertForm from '../components/ConcertForm';
import AnalyticsPanel from '../components/admin/AnalyticsPanel';
import FeedIntegrationPanel from '../components/operator/FeedIntegrationPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getCSRFToken = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf_token') return decodeURIComponent(value);
  }
  return null;
};

const secureFetch = async (url, options = {}) => {
  const method = options.method?.toUpperCase() || 'GET';
  const headers = { ...options.headers };
  // Backward-compat: send legacy localStorage token as Bearer if present.
  // Backend prefers httpOnly cookie when both available.
  const legacyToken = localStorage.getItem('token');
  if (legacyToken && !headers['Authorization']) headers['Authorization'] = `Bearer ${legacyToken}`;
  // CSRF for state-changing requests
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrf = getCSRFToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  // credentials: 'include' sends the httpOnly access_token cookie
  return fetch(url, { ...options, headers, credentials: 'include' });
};

// Reuse PackageForm from DashboardPage via dynamic import would be complex,
// so we create a focused operator package form
const OperatorPackageForm = ({ initialData, onSave, onCancel, loading, continents = [], countries = [] }) => {
  const EMPTY = {
    title: '', price: '', old_price: '', duration: '', departure_dates: [],
    short_description: '', operator: '', redirect_link: '', includes: [],
    includes_note: '', excludes: [], optional_excursions: [], optional_excursions_note: '',
    detailed_program: [], useful_info: '', category: '', continent: '', country: '',
    image_url: '', is_early_booking: false, is_last_minute: false, transport_type: 'avion',
    is_active: true, departure_city: '',
    cities_visited: [], circuit_type: '', available_months: [], highlights: [],
    difficulty_level: 'moderat', group_size: '', guide_language: 'romana', keywords: []
  };

  const [form, setForm] = useState(initialData || EMPTY);
  const [errors, setErrors] = useState({});
  const [newCity, setNewCity] = useState('');
  const [newHighlight, setNewHighlight] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [newInclude, setNewInclude] = useState('');
  const [newExclude, setNewExclude] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newExcursion, setNewExcursion] = useState({ name: '', price: '' });
  const [newProgramDay, setNewProgramDay] = useState({ day: '', title: '', description: '', accommodation: '', meals: '' });

  useEffect(() => {
    if (initialData) {
      // Normalize transport_type to lowercase
      const normalized = {...initialData};
      if (normalized.transport_type) normalized.transport_type = normalized.transport_type.toLowerCase();
      setForm(normalized);
    }
    else setForm(EMPTY);
  }, [initialData]);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const addToList = (field, value, setter) => {
    if (value.trim()) {
      setForm(prev => ({ ...prev, [field]: [...(prev[field] || []), value.trim()] }));
      setter('');
    }
  };

  const removeFromList = (field, index) => {
    setForm(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  const validate = () => {
    const e = {};
    if (!form.title?.trim()) e.title = 'Titlul este obligatoriu';
    if (!form.price?.trim()) e.price = 'Prețul este obligatoriu';
    if (!form.duration?.trim()) e.duration = 'Durata este obligatorie';
    if (!form.short_description?.trim()) e.short_description = 'Descrierea este obligatorie';
    if (!form.redirect_link?.trim()) e.redirect_link = 'Link-ul este obligatoriu';
    if (!form.category) e.category = 'Categoria este obligatorie';
    if (!form.image_url?.trim()) e.image_url = 'Imaginea este obligatorie';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onSave(form);
  };

  const MONTHS = ['ianuarie','februarie','martie','aprilie','mai','iunie','iulie','august','septembrie','octombrie','noiembrie','decembrie'];

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-xl p-6 border border-slate-200" data-testid="operator-package-form">
      <h3 className="text-lg font-bold text-slate-800">{initialData?.id ? 'Editare Program' : 'Program Nou'}</h3>

      {/* Basic fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-slate-700 mb-1">Titlu *</label>
          <input type="text" value={form.title} onChange={e => set('title', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg text-slate-800 ${errors.title ? 'border-red-400' : 'border-slate-300'}`}
            placeholder="ex: Circuit Cultural Italia" data-testid="pkg-title" />
          {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Preț *</label>
          <input type="text" value={form.price} onChange={e => set('price', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg text-slate-800 ${errors.price ? 'border-red-400' : 'border-slate-300'}`}
            placeholder="999 EUR" data-testid="pkg-price" />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Preț vechi</label>
          <input type="text" value={form.old_price} onChange={e => set('old_price', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800" placeholder="1199 EUR" />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Durată *</label>
          <input type="text" value={form.duration} onChange={e => set('duration', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg text-slate-800 ${errors.duration ? 'border-red-400' : 'border-slate-300'}`}
            placeholder="7 zile / 6 nopți" data-testid="pkg-duration" />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Categorie *</label>
          <select value={form.category} onChange={e => set('category', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg text-slate-800 ${errors.category ? 'border-red-400' : 'border-slate-300'}`}
            data-testid="pkg-category">
            <option value="">Selectează...</option>
            <option value="Circuite Externe">Circuite Externe</option>
            <option value="Circuite Romania">Circuite Romania</option>
            <option value="City Break">City Break</option>
            <option value="Concerte">Concerte</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Transport</label>
          <select value={form.transport_type} onChange={e => set('transport_type', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800">
            <option value="avion">Avion</option>
            <option value="autocar">Autocar</option>
            <option value="individual">Individual</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Continent</label>
          <select value={form.continent} onChange={e => { set('continent', e.target.value); set('country', ''); }}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800">
            <option value="">Selectează...</option>
            {continents.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Țară</label>
          <select value={form.country} onChange={e => set('country', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800">
            <option value="">Selectează...</option>
            {countries.filter(c => !form.continent || c.continent === form.continent || c.continent_name === form.continent).map(c => (
              <option key={c.id || c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Plecare din</label>
          <input type="text" value={form.departure_city || ''} onChange={e => set('departure_city', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800" placeholder="București" />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Link afiliat *</label>
          <input type="text" value={form.redirect_link} onChange={e => set('redirect_link', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg text-slate-800 ${errors.redirect_link ? 'border-red-400' : 'border-slate-300'}`}
            placeholder="https://..." data-testid="pkg-redirect-link" />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">URL Imagine *</label>
          <input type="text" value={form.image_url} onChange={e => set('image_url', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg text-slate-800 ${errors.image_url ? 'border-red-400' : 'border-slate-300'}`}
            placeholder="https://images.unsplash.com/..." data-testid="pkg-image" />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-slate-700 mb-1">Descriere scurtă *</label>
          <textarea value={form.short_description} onChange={e => set('short_description', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg text-slate-800 h-24 ${errors.short_description ? 'border-red-400' : 'border-slate-300'}`}
            placeholder="Descriere..." data-testid="pkg-description" />
        </div>
      </div>

      {/* Date plecare */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Date plecare</label>
        <div className="flex gap-2">
          <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-slate-800" />
          <button type="button" onClick={() => addToList('departure_dates', newDate, setNewDate)}
            className="px-3 py-2 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600">Adaugă</button>
        </div>
        {form.departure_dates?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {form.departure_dates.map((d, i) => (
              <span key={i} className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full flex items-center gap-1">
                {d} <button type="button" onClick={() => removeFromList('departure_dates', i)} className="hover:text-red-500">&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Circuit-specific fields */}
      {(form.category === 'Circuite Externe' || form.category === 'Circuite Romania') && (
        <div className="border-t border-slate-200 pt-4 space-y-4">
          <h4 className="text-sm font-bold text-amber-600 uppercase tracking-wider">Detalii Circuit</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tip Circuit</label>
              <select value={form.circuit_type || ''} onChange={e => set('circuit_type', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800" data-testid="pkg-circuit-type">
                <option value="">Selectează...</option>
                {['cultural','aventura','gastronomic','religios','romantic','familie','mixt','luxury','sport','natura'].map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nivel dificultate</label>
              <select value={form.difficulty_level || 'moderat'} onChange={e => set('difficulty_level', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800">
                <option value="ușor">Ușor</option>
                <option value="moderat">Moderat</option>
                <option value="intens">Intens</option>
              </select>
            </div>
          </div>

          {/* Cities visited */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Orașe vizitate</label>
            <div className="flex gap-2">
              <input type="text" value={newCity} onChange={e => setNewCity(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addToList('cities_visited', newCity, setNewCity); }}}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-slate-800" placeholder="ex: Roma" data-testid="pkg-city-input" />
              <button type="button" onClick={() => addToList('cities_visited', newCity, setNewCity)}
                className="px-3 py-2 bg-slate-700 text-white rounded-lg text-sm">+</button>
            </div>
            {form.cities_visited?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {form.cities_visited.map((c, i) => (
                  <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center gap-1">
                    {c} <button type="button" onClick={() => removeFromList('cities_visited', i)} className="hover:text-red-500">&times;</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Highlights */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Atracții principale</label>
            <div className="flex gap-2">
              <input type="text" value={newHighlight} onChange={e => setNewHighlight(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addToList('highlights', newHighlight, setNewHighlight); }}}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-slate-800" placeholder="ex: Colosseum" />
              <button type="button" onClick={() => addToList('highlights', newHighlight, setNewHighlight)}
                className="px-3 py-2 bg-slate-700 text-white rounded-lg text-sm">+</button>
            </div>
            {form.highlights?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {form.highlights.map((h, i) => (
                  <span key={i} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center gap-1">
                    {h} <button type="button" onClick={() => removeFromList('highlights', i)} className="hover:text-red-500">&times;</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Available months */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Luni disponibile</label>
            <div className="flex flex-wrap gap-2">
              {MONTHS.map(m => (
                <button key={m} type="button"
                  onClick={() => {
                    const months = form.available_months || [];
                    if (months.includes(m)) set('available_months', months.filter(x => x !== m));
                    else set('available_months', [...months, m]);
                  }}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    form.available_months?.includes(m) ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}>
                  {m.charAt(0).toUpperCase() + m.slice(1, 3)}
                </button>
              ))}
            </div>
          </div>

          {/* Keywords */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Keywords AI</label>
            <div className="flex gap-2">
              <input type="text" value={newKeyword} onChange={e => setNewKeyword(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addToList('keywords', newKeyword, setNewKeyword); }}}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-slate-800" placeholder="ex: romantic, aventura" />
              <button type="button" onClick={() => addToList('keywords', newKeyword, setNewKeyword)}
                className="px-3 py-2 bg-slate-700 text-white rounded-lg text-sm">+</button>
            </div>
            {form.keywords?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {form.keywords.map((k, i) => (
                  <span key={i} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full flex items-center gap-1">
                    {k} <button type="button" onClick={() => removeFromList('keywords', i)} className="hover:text-red-500">&times;</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Include / Exclude */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Include</label>
          <div className="flex gap-2">
            <input type="text" value={newInclude} onChange={e => setNewInclude(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addToList('includes', newInclude, setNewInclude); }}}
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" placeholder="ex: Transport avion" />
            <button type="button" onClick={() => addToList('includes', newInclude, setNewInclude)}
              className="px-2 py-1 bg-slate-600 text-white rounded-lg text-xs">+</button>
          </div>
          {form.includes?.map((item, i) => (
            <div key={i} className="flex justify-between items-center mt-1 text-xs text-slate-600">
              <span>+ {item}</span>
              <button type="button" onClick={() => removeFromList('includes', i)} className="text-red-400 hover:text-red-600">&times;</button>
            </div>
          ))}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Exclude</label>
          <div className="flex gap-2">
            <input type="text" value={newExclude} onChange={e => setNewExclude(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addToList('excludes', newExclude, setNewExclude); }}}
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" placeholder="ex: Excursii opționale" />
            <button type="button" onClick={() => addToList('excludes', newExclude, setNewExclude)}
              className="px-2 py-1 bg-slate-600 text-white rounded-lg text-xs">+</button>
          </div>
          {form.excludes?.map((item, i) => (
            <div key={i} className="flex justify-between items-center mt-1 text-xs text-slate-600">
              <span>- {item}</span>
              <button type="button" onClick={() => removeFromList('excludes', i)} className="text-red-400 hover:text-red-600">&times;</button>
            </div>
          ))}
        </div>
      </div>

      {/* Excursii opționale */}
      <div className="border-t border-slate-200 pt-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">Excursii opționale</label>
        <div className="flex gap-2 mb-2">
          <input type="text" value={newExcursion.name} onChange={e => setNewExcursion(p => ({...p, name: e.target.value}))}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" placeholder="Numele excursiei" />
          <input type="text" value={newExcursion.price} onChange={e => setNewExcursion(p => ({...p, price: e.target.value}))}
            className="w-28 px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" placeholder="Preț (€)" />
          <button type="button" onClick={() => {
            if (newExcursion.name.trim()) {
              setForm(prev => ({...prev, optional_excursions: [...(prev.optional_excursions || []), {...newExcursion}]}));
              setNewExcursion({ name: '', price: '' });
            }
          }} className="px-3 py-2 bg-slate-600 text-white rounded-lg text-xs">+</button>
        </div>
        {form.optional_excursions?.map((exc, i) => (
          <div key={i} className="flex justify-between items-center text-xs text-slate-600 py-1">
            <span>{exc.name} {exc.price && `— ${exc.price}`}</span>
            <button type="button" onClick={() => removeFromList('optional_excursions', i)} className="text-red-400 hover:text-red-600">&times;</button>
          </div>
        ))}
      </div>

      {/* Program detaliat pe zile */}
      <div className="border-t border-slate-200 pt-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">Program detaliat pe zile</label>
        <div className="space-y-2 mb-3">
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={newProgramDay.day} onChange={e => setNewProgramDay(p => ({...p, day: e.target.value}))}
              className="px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" placeholder="Ziua (ex: Ziua 1)" />
            <input type="text" value={newProgramDay.title} onChange={e => setNewProgramDay(p => ({...p, title: e.target.value}))}
              className="px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" placeholder="Titlu (ex: Sosire Roma)" />
          </div>
          <textarea value={newProgramDay.description} onChange={e => setNewProgramDay(p => ({...p, description: e.target.value}))}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm h-16" placeholder="Descriere detaliată a zilei..." />
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={newProgramDay.accommodation} onChange={e => setNewProgramDay(p => ({...p, accommodation: e.target.value}))}
              className="px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" placeholder="Cazare (ex: Hotel 4*)" />
            <input type="text" value={newProgramDay.meals} onChange={e => setNewProgramDay(p => ({...p, meals: e.target.value}))}
              className="px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" placeholder="Mese (ex: Mic dejun)" />
          </div>
          <button type="button" onClick={() => {
            if (newProgramDay.day.trim() || newProgramDay.title.trim()) {
              setForm(prev => ({...prev, detailed_program: [...(prev.detailed_program || []), {...newProgramDay}]}));
              setNewProgramDay({ day: '', title: '', description: '', accommodation: '', meals: '' });
            }
          }} className="px-4 py-2 bg-slate-600 text-white rounded-lg text-sm">Adaugă ziua</button>
        </div>
        {form.detailed_program?.map((day, i) => (
          <div key={i} className="bg-slate-50 rounded-lg p-3 mb-2 text-sm">
            <div className="flex justify-between items-start">
              <div>
                <span className="font-bold text-slate-800">{day.day || `Ziua ${i+1}`}</span>
                {day.title && <span className="text-slate-600"> — {day.title}</span>}
                {day.description && <p className="text-slate-500 text-xs mt-1">{day.description.substring(0, 100)}...</p>}
                <div className="text-xs text-slate-400 mt-0.5">
                  {day.accommodation && <span>Cazare: {day.accommodation}</span>}
                  {day.meals && <span className="ml-2">Mese: {day.meals}</span>}
                </div>
              </div>
              <button type="button" onClick={() => removeFromList('detailed_program', i)} className="text-red-400 hover:text-red-600 text-lg">&times;</button>
            </div>
          </div>
        ))}
      </div>

      {/* Informații utile */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Informații utile</label>
        <textarea value={form.useful_info || ''} onChange={e => set('useful_info', e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 h-20 text-sm"
          placeholder="Informații suplimentare: documente necesare, vize, vaccinări..." />
      </div>

      {/* Status toggles */}
      <div className="border-t border-slate-200 pt-4 flex flex-wrap gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)}
            className="rounded border-slate-300" />
          <span className="text-sm text-slate-700">Activ</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_early_booking} onChange={e => set('is_early_booking', e.target.checked)}
            className="rounded border-slate-300" />
          <span className="text-sm text-slate-700">Early Booking</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_last_minute} onChange={e => set('is_last_minute', e.target.checked)}
            className="rounded border-slate-300" />
          <span className="text-sm text-slate-700">Last Minute</span>
        </label>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
        <button type="button" onClick={onCancel}
          className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors" data-testid="pkg-cancel">
          Anulează
        </button>
        <button type="submit" disabled={loading}
          className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="pkg-save">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {initialData?.id ? 'Salvează modificările' : 'Adaugă programul'}
        </button>
      </div>
    </form>
  );
};

const STATUS_COLORS = {
  approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Aprobat' },
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'In așteptare' },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Respins' },
};

// Inline price editing component
const InlinePrice = ({ pkg, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(pkg.price || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!price.trim() || price === pkg.price) { setEditing(false); return; }
    setSaving(true);
    try {
      await onUpdate(pkg.id, price.trim());
      setEditing(false);
    } catch (err) {
      console.error('[Operator] Inline price update failed:', err);
    }
    setSaving(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input type="text" value={price} onChange={e => setPrice(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setPrice(pkg.price); } }}
          className="w-24 px-2 py-1 border border-amber-400 rounded text-sm font-bold text-slate-800 focus:outline-none"
          autoFocus />
        <button onClick={save} disabled={saving} className="text-green-600 hover:text-green-700"><CheckCircle className="w-4 h-4" /></button>
        <button onClick={() => { setEditing(false); setPrice(pkg.price); }} className="text-red-400 hover:text-red-500"><XCircle className="w-3.5 h-3.5" /></button>
      </div>
    );
  }

  return (
    <div className="text-sm font-bold text-amber-600 whitespace-nowrap cursor-pointer hover:underline" onClick={() => setEditing(true)} title="Click pentru a edita prețul">
      {pkg.price}
    </div>
  );
};

export default function OperatorDashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [packages, setPackages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showConcertForm, setShowConcertForm] = useState(false);
  const [editingPkg, setEditingPkg] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [continents, setContinents] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auth check
  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/'); return; }
    const u = JSON.parse(stored);
    if (u.role !== 'operator') { navigate('/'); return; }
    setUser(u);
  }, [navigate]);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [statsRes, pkgsRes] = await Promise.all([
        secureFetch(`${API_URL}/api/operator/stats`),
        secureFetch(`${API_URL}/api/operator/packages`)
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (pkgsRes.ok) {
        const data = await pkgsRes.json();
        setPackages(data.packages || []);
      }

      // Load geo data for form (public endpoints, but include credentials for consistency)
      const [contRes, countRes] = await Promise.all([
        secureFetch(`${API_URL}/api/continents`).catch(() => null),
        secureFetch(`${API_URL}/api/countries`).catch(() => null)
      ]);
      if (contRes?.ok) setContinents(await contRes.json());
      if (countRes?.ok) setCountries(await countRes.json());
    } catch (e) {
      console.error('Load error:', e);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      const url = editingPkg?.id
        ? `${API_URL}/api/operator/packages/${editingPkg.id}`
        : `${API_URL}/api/operator/packages`;
      const method = editingPkg?.id ? 'PUT' : 'POST';

      // Set operator name from user
      if (!data.operator && user?.operator_name) data.operator = user.operator_name;

      const res = await secureFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Eroare la salvare');
      }

      const saved = await res.json();
      const statusMsg = saved.approval_status === 'pending'
        ? 'Programul a fost trimis pentru aprobare!'
        : 'Programul a fost publicat cu succes!';
      toast.success(editingPkg?.id ? 'Program actualizat!' : statusMsg);
      setShowForm(false);
      setShowConcertForm(false);
      setEditingPkg(null);
      loadData();
    } catch (e) {
      toast.error(e.message);
    }
    setFormLoading(false);
  };

  const handleDelete = async (pkgId) => {
    if (!window.confirm('Sigur vrei să ștergi acest program?')) return;
    try {
      const res = await secureFetch(`${API_URL}/api/operator/packages/${pkgId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Ștergere eșuată');
      toast.success('Program șters');
      loadData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleInlinePriceUpdate = async (pkgId, newPrice) => {
    try {
      const res = await secureFetch(`${API_URL}/api/operator/packages/${pkgId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price: newPrice })
      });
      if (!res.ok) throw new Error('Eroare');
      toast.success('Preț actualizat');
      loadData();
    } catch (e) {
      toast.error('Eroare la actualizare preț');
      throw e;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="operator-dashboard">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="text-xl font-bold">
              <span className="text-slate-800">Va</span><span className="text-amber-500">Ai</span>
            </a>
            <span className="text-slate-300">|</span>
            <span className="text-sm font-medium text-slate-600">{user.operator_name || 'Operator'}</span>
          </div>
          <button onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-red-500 transition-colors" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Deconectare
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome + Stats */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="operator-welcome">
            Bine ai venit, {user.name}!
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {user.can_publish_directly
              ? 'Programele tale se publică direct pe site.'
              : 'Programele tale necesită aprobare înainte de publicare.'}
          </p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
              <Package className="w-5 h-5 mx-auto text-slate-400 mb-1" />
              <div className="text-2xl font-bold text-slate-800">{stats.total_packages}</div>
              <div className="text-xs text-slate-500">Total programe</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-green-200 text-center">
              <CheckCircle className="w-5 h-5 mx-auto text-green-500 mb-1" />
              <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
              <div className="text-xs text-slate-500">Aprobate</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-amber-200 text-center">
              <Clock className="w-5 h-5 mx-auto text-amber-500 mb-1" />
              <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
              <div className="text-xs text-slate-500">In așteptare</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-red-200 text-center">
              <XCircle className="w-5 h-5 mx-auto text-red-500 mb-1" />
              <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
              <div className="text-xs text-slate-500">Respinse</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
              <BarChart3 className="w-5 h-5 mx-auto text-slate-400 mb-1" />
              <div className="text-2xl font-bold text-slate-800">{stats.total_clicks}</div>
              <div className="text-xs text-slate-500">Click-uri</div>
            </div>
          </div>
        )}

        {/* Add new button */}
        {!showForm && !showConcertForm && (
          <div className="flex gap-3">
            <button onClick={() => { setEditingPkg(null); setShowForm(true); setShowConcertForm(false); }}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors" data-testid="add-package-btn">
              <Plus className="w-4 h-4" /> Adaugă Program Nou
            </button>
            <button onClick={() => { setEditingPkg(null); setShowConcertForm(true); setShowForm(false); }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors" data-testid="add-concert-btn">
              <Music className="w-4 h-4" /> Adaugă Concert / Eveniment
            </button>
          </div>
        )}

        {/* Package Form */}
        {showForm && (
          <OperatorPackageForm
            initialData={editingPkg}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditingPkg(null); }}
            loading={formLoading}
            continents={continents}
            countries={countries}
          />
        )}

        {/* Concert Form */}
        {showConcertForm && (
          <ConcertForm
            initialData={editingPkg}
            onSave={handleSave}
            onCancel={() => { setShowConcertForm(false); setEditingPkg(null); }}
            loading={formLoading}
          />
        )}

        {/* Packages list */}
        {!showForm && !showConcertForm && (
          <div className="bg-white rounded-xl border border-slate-200" data-testid="packages-list">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-bold text-slate-800">Programele mele ({packages.length})</h2>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : packages.length === 0 ? (
              <div className="py-12 text-center text-slate-400">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nu ai programe încă. Adaugă primul tău program!</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {packages.map(pkg => {
                  const status = STATUS_COLORS[pkg.approval_status] || STATUS_COLORS.pending;
                  return (
                    <div key={pkg.id} className="px-4 py-3 flex items-center gap-4 hover:bg-slate-50 transition-colors" data-testid={`package-row-${pkg.id}`}>
                      <img src={pkg.image_url} alt={pkg.title} className="w-14 h-10 rounded object-cover flex-shrink-0" 
                        onError={e => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 70"><rect fill="%23e2e8f0" width="100" height="70"/></svg>'; }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 truncate">{pkg.title}</div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                          <span>{pkg.category}</span>
                          <span>&middot;</span>
                          <span>{pkg.country || pkg.city || '-'}</span>
                          <span>&middot;</span>
                          <span>{pkg.category === 'Concerte' ? (pkg.artist_name || '-') : pkg.duration}</span>
                          {pkg.music_genre && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[10px]">{pkg.music_genre}</span>}
                        </div>
                      </div>
                      <InlinePrice pkg={pkg} onUpdate={handleInlinePriceUpdate} />
                      <div className="text-center min-w-[50px]" title="Accesări">
                        <div className="text-sm font-bold text-slate-700">{pkg.click_count || 0}</div>
                        <div className="text-[10px] text-slate-400">click-uri</div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`} data-testid={`status-${pkg.id}`}>
                        {status.label}
                      </span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => { 
                          setEditingPkg(pkg); 
                          if (pkg.category === 'Concerte') { setShowConcertForm(true); setShowForm(false); }
                          else { setShowForm(true); setShowConcertForm(false); }
                        }}
                          className="p-1.5 text-slate-400 hover:text-amber-500 transition-colors" title="Editează" data-testid={`edit-${pkg.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(pkg.id)}
                          className="p-1.5 text-slate-400 hover:text-red-500 transition-colors" title="Șterge" data-testid={`delete-${pkg.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Analytics Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="operator-analytics">
          <h2 className="font-bold text-slate-800 text-lg mb-4">Statistici detaliate</h2>
          <AnalyticsPanel />
        </div>

        {/* Feed Integration Section */}
        <FeedIntegrationPanel secureFetch={secureFetch} />
      </main>
    </div>
  );
}
